import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Typography, Box, Tooltip } from '@mui/material';
import EventDialog from './EventDialog';
import { getDatabase, ref, get } from 'firebase/database';
import fc from '../firebaseConfig';

const db = getDatabase(fc);

const CustomDayView = ({ events, employees, onSave, onDelete, currentDate }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [columnWidth, setColumnWidth] = useState(100);
  const [productDetails, setProductDetails] = useState({});

  useEffect(() => {
    const employeeCount = employees.length;
    const newColumnWidth = (employeeCount > 0 ? Math.floor(95 / (employeeCount)) : 100);
    console.log(newColumnWidth);
    setColumnWidth(newColumnWidth);
  }, [employees]);

  const fetchProductDetails = useCallback(async (productId) => {
    if (productId) {
      try {
        const productRef = ref(db, `products/${productId}`);
        const snapshot = await get(productRef);
        if (snapshot.exists()) {
          setProductDetails(prevDetails => ({
            ...prevDetails,
            [productId]: snapshot.val()
          }));
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
      }
    }
  }, []);

  useEffect(() => {
    // Fetch product details for all unique productIds in events
    const uniqueProductIds = [...new Set(events.map(event => event.productId))];
    uniqueProductIds.forEach(fetchProductDetails);
  }, [events, fetchProductDetails]);

  const handleOpenDialog = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleSave = (updatedEvent) => {
    onSave(updatedEvent);
    handleCloseDialog();
  };

  const handleDelete = () => {
    onDelete(selectedEvent.id);
    handleCloseDialog();
  };

  const { startHour, endHour } = useMemo(() => {
    if (events.length === 0) {
      return { startHour: 5, endHour: 23 };
    }
    let minHour = 23;
    let maxHour = 0;
    events.forEach(event => {
      const startHour = new Date(event.startDate).getHours();
      const endHour = new Date(event.endDate).getHours();
      minHour = Math.min(minHour, startHour);
      maxHour = Math.max(maxHour, endHour);
    });
    return {
      startHour: Math.max(5, Math.min(23, minHour - 1)),
      endHour: 23
    };
  }, [events]);

  const totalHours = endHour - startHour + 1;
  const hourHeight = 60;
  const headerHeight = 46;

  const getEventPosition = (event) => {
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    const top = ((start.getHours() - startHour + start.getMinutes() / 60) * hourHeight) + 1;
    const height = ((end.getHours() - start.getHours()) + (end.getMinutes() - start.getMinutes()) / 60) * hourHeight - 9; // Reduce height by 5px
    return { top, height };
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden', marginTop: '10px', marginLeft: '1rem' }}>
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', minWidth: 'fit-content', position: 'relative' }}>
          <Box sx={{ width: '50px', flexShrink: 0, position: 'relative', zIndex: 1 }}>
            <Box sx={{ height: `${headerHeight}px` }} />
            {Array.from({ length: totalHours }, (_, index) => (
              <Box key={index} sx={{ height: `${hourHeight}px`, position: 'relative' }}>
                <Typography variant="caption" sx={{ position: 'absolute', top: '-10px', left: '2px' }}>
                  {(startHour + index).toString().padStart(2, '0')}:00
                </Typography>
              </Box>
            ))}
          </Box>
          {employees.map((employee) => (
            <Box
              key={employee.id}
              sx={{
                flexShrink: 0,
                width: `${columnWidth}%`,
                position: 'relative',
                borderRight: '1px solid #ccc',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  p: 1,
                  textAlign: 'center',
                  borderBottom: '1px solid #ccc',
                  height: `${headerHeight - 16}px`,
                  overflow: 'hidden',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}
              >
                {employee.name}
              </Typography>
              <Box sx={{ position: 'relative', height: `${totalHours * hourHeight}px` }}>
                {Array.from({ length: totalHours * 4 }, (_, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'absolute',
                      top: `${index * (hourHeight / 4)}px`,
                      left: 0,
                      right: 0,
                      height: '1px',
                      backgroundColor: index % 4 === 0 ? '#ccc' : '#eee',
                      zIndex: 0
                    }}
                  />
                ))}
                {events
                  .filter((event) => event.employeeName === employee.name)
                  .map((event) => {
                    const { top, height } = getEventPosition(event);
                    const textColor = 'white';
                    
                    // Get product details
                    const product = productDetails[event.productId];
                    const productInfo = product
                      ? `${product.name}: ${event.productQuantity}${product.type}`
                      : 'BRAK PRODUKTU W TEJ USŁUDZE';

                    const tooltipContent = (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: textColor }}>
                          {event.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: textColor }}>
                          {new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}&nbsp;-&nbsp;
                          {new Date(event.endDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        <Typography variant="body2" sx={{ color: textColor }}>
                          klient: {event.name}, {event.phone}
                        </Typography>
                        <Typography variant="body2" sx={{ color: textColor }}>
                          pracownik: {event.employeeName}, {event.employeeNumber}
                        </Typography>
                        <Typography variant="body2" sx={{ color: textColor }}>
                          produkt: {productInfo}
                        </Typography>
                        <Typography variant="body2" sx={{ color: textColor }}>
                          cena: {event.price}
                        </Typography>
                      </>
                    );

                    return (
                      <Tooltip key={event.id} title={tooltipContent} arrow placement="top">
                        <Box
                          sx={{
                            position: 'absolute',
                            left: '2px',
                            right: '2px',
                            top: `${top}px`,
                            height: `${height}px`,
                            backgroundColor: event.color,
                            borderRadius: '4px',
                            padding: '4px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#6d6d6d',
                            },
                          }}
                          onClick={() => handleOpenDialog(event)}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: textColor }}>
                            {event.title}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: textColor }}>
                            {new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}&nbsp;-&nbsp;
                            {new Date(event.endDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      {selectedEvent && (
        <EventDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          event={selectedEvent}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </Box>
  );
};

export default CustomDayView;
